"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useActiveStore } from "@/store/activeStore";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import { Skeleton } from "@/components/ui/Skeleton";
import { ActiveStoreSelector } from "@/components/dashboard/ActiveStoreSelector";

/**
 * /map — 동네 레이더 (사용자 결정 2026-05-09, 4-3).
 *
 * 본인 매장 좌표 기준 반경 N km LOCALDATA 카페 분포 + 신규/폐업 시각화.
 *
 * 4 분기 UX:
 *   비로그인       → 가입 유도
 *   매장 미등록    → 매장 등록 CTA
 *   좌표 미등록    → 매장 수정 (주소) CTA
 *   정상           → 카카오맵 + 핀 + 반경 슬라이더 + 통계
 */

interface NeighborhoodPlace {
  id: number;
  mgmt_no: string;
  biz_name: string;
  biz_category: string | null;
  service_type: "rest_cafes" | "bakery";
  address_road: string | null;
  latitude: number;
  longitude: number;
  distance_m: number;
  approval_date: string | null;
  closure_date: string | null;
  biz_status: string | null;
  is_new: boolean;
  is_closed_recent: boolean;
}

// #8 상권 분석 — 4 신규 카드 응답 필드 (사용자 결정 2026-05-10)
interface NeighborhoodDemographics {
  region_name: string | null;
  total_population: number | null;
  male_pct: number | null;
  female_pct: number | null;
  age_under20_pct: number | null;
  age_20s_pct: number | null;
  age_30s_pct: number | null;
  age_40s_pct: number | null;
  age_50s_pct: number | null;
  age_60plus_pct: number | null;
  period: string | null;            // "202604"
}
interface NeighborhoodBusiness {
  radius_km: number;
  cafe_count: number;
  bakery_count: number;       // 5/11 추가 (I21001 빵/도넛)
  restaurant_count: number;
  korean_count: number;
  pub_count: number;
}
interface NeighborhoodLivingPopHour {
  hour: number;
  population: number | null;
  age_20s_pct: number | null;
  age_30s_pct: number | null;
  age_40s_pct: number | null;
}
interface NeighborhoodLivingPopulation {
  available: boolean;
  hourly: NeighborhoodLivingPopHour[];
  avg_population: number | null;
  note: string | null;
}
interface NeighborhoodRent {
  available: boolean;
  zone_full_name: string | null;
  distance_km: number | null;
  rent_krw_per_m2: number | null;   // 천원/㎡
  vacancy_pct: number | null;
  period: string | null;             // "202502"
  is_fallback: boolean;
}

interface NeighborhoodOut {
  store_id: number;
  store_name: string;
  center: { latitude: number | null; longitude: number | null };
  radius_km: number;
  has_coord: boolean;
  places: NeighborhoodPlace[];
  // 5/11 #1 fix — places cap (500) 무관한 별도 list
  recent_new?: NeighborhoodPlace[];
  recent_closed?: NeighborhoodPlace[];
  stats: { total_active: number; new_3m: number; closed_3m: number };
  data_freshness_note: string;
  // 신규 (5/10)
  demographics?: NeighborhoodDemographics | null;
  business?: NeighborhoodBusiness | null;
  living_population?: NeighborhoodLivingPopulation | null;
  rent?: NeighborhoodRent | null;
}

interface StoreListOut {
  items: { id: number; name: string }[];
}

const KAKAO_JS_KEY = process.env.NEXT_PUBLIC_KAKAO_JS_KEY ?? "";

const RADIUS_OPTIONS = [0.5, 1, 2, 3, 5];   // km

/** 사업자 개시일자 → 영업기간 (예: '5년 3개월', '2개월') */
function yearsSince(approvalDate: string): string {
  // approvalDate 형식: 'YYYY-MM-DD' 또는 'YYYYMMDD'
  const s = approvalDate.replace(/-/g, "");
  if (s.length < 8) return "";
  const y = parseInt(s.slice(0, 4));
  const m = parseInt(s.slice(4, 6));
  const d = parseInt(s.slice(6, 8));
  if (!y || !m || !d) return "";
  const start = new Date(y, m - 1, d);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (now.getDate() < start.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  if (years <= 0 && months <= 0) return "신규";
  if (years <= 0) return `${months}개월`;
  if (months === 0) return `${years}년`;
  return `${years}년 ${months}개월`;
}

/** 거리 m → '320m' / '1.2km' */
function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)}m`;
  return `${(m / 1000).toFixed(1)}km`;
}

// 핀 색상 — 영업·신규·폐업 구분
const PIN_COLOR = {
  new: "#10B981",       // 신규 = emerald
  closed: "#EF4444",    // 폐업 = red
  active: "#3B82F6",    // 일반 영업 = blue
  myStore: "#F59E0B",   // 본인 매장 = amber
};

export default function MapPage() {
  const { token, isLoggedIn, user } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const { activeStoreId, setActiveStoreId } = useActiveStore();
  const beta = useBetaStatus();
  // 베타 활성 동안 모두 풀, 종료 후 free_auth 는 진입 OK + 카드 블러 (사용자 결정 2026-05-22)
  const betaActive = beta?.beta_active ?? true;
  const isPaidLike = !!user && (user.tier === "paid" || user.tier === "franchise");
  const hasFullAccess = betaActive || isPaidLike;

  const [data, setData] = useState<NeighborhoodOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"no_store" | "no_coord" | "fetch_error" | null>(null);
  const [radiusKm, setRadiusKm] = useState(1.0);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  // #9 모바일 핀 tap — 매장명 popup (사용자 결정 2026-05-11)
  const [selectedPin, setSelectedPin] = useState<NeighborhoodPlace | null>(null);
  // #4 빈 지도 클릭 통일 — PC/모바일 모두 popup 후 사용자 확정 (사용자 결정 2026-05-11)
  const [selectedBlank, setSelectedBlank] = useState<{ lat: number; lng: number; addrName: string } | null>(null);
  // 핀 클릭과 빈 지도 click 이벤트가 순차 발생 (kakao map click 은 DOM bubble 별개) — ref 로 200ms 무시
  const pinJustClickedRef = useRef<boolean>(false);
  // 2026-05-12 — 통계 카드 클릭 시 마커 필터 (all = 전체 / active / new / closed)
  const [pinFilter, setPinFilter] = useState<"all" | "active" | "new" | "closed">("all");

  // 5/11 #4 — 검색 모드 (다른 지역)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCoord, setSearchCoord] = useState<{ lat: number; lng: number; label: string } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const overlaysRef = useRef<any[]>([]);
  const [sdkReady, setSdkReady] = useState(false);

  // 카카오맵 SDK 동적 load
  useEffect(() => {
    if (!KAKAO_JS_KEY) return;
    if ((window as any).kakao?.maps) {
      setSdkReady(true);
      return;
    }
    const SCRIPT_ID = "kakao-maps-sdk";
    if (document.getElementById(SCRIPT_ID)) return;
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_JS_KEY}&libraries=services&autoload=false`;
    script.async = true;
    script.onload = () => {
      (window as any).kakao.maps.load(() => setSdkReady(true));
    };
    document.body.appendChild(script);
  }, []);

  // 데이터 fetch (반경 변경 시 재호출)
  useEffect(() => {
    if (!hasHydrated) return;
    if (!isLoggedIn || !token) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = (storeId: number) => {
      setLoading(true);
      setError(null);
      // 5/11 #4 — 검색 모드 시 lat/lng/label 추가
      const params = new URLSearchParams({ radius_km: String(radiusKm) });
      if (searchCoord) {
        params.set("lat", String(searchCoord.lat));
        params.set("lng", String(searchCoord.lng));
        params.set("label", searchCoord.label);
      }
      axios
        .get<NeighborhoodOut>(`/api/stores/${storeId}/neighborhood?${params}`, { headers })
        .then((r) => {
          if (cancelled) return;
          setData(r.data);
          if (!r.data.has_coord) setError("no_coord");
        })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          if (status === 404 || status === 403) {
            // 옛 activeStoreId 잔존 — 매장 리스트 재조회
            axios
              .get<StoreListOut>("/api/stores", { headers })
              .then((r2) => {
                if (cancelled) return;
                if (!r2.data.items || r2.data.items.length === 0) {
                  setActiveStoreId(null);
                  setError("no_store");
                  setLoading(false);
                  return;
                }
                const firstId = r2.data.items[0].id;
                setActiveStoreId(firstId);
                fetchData(firstId);
              })
              .catch(() => {
                if (!cancelled) {
                  setError("fetch_error");
                  setLoading(false);
                }
              });
          } else {
            setError("fetch_error");
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    if (activeStoreId) {
      fetchData(activeStoreId);
    } else {
      setLoading(true);
      axios
        .get<StoreListOut>("/api/stores", { headers })
        .then((r) => {
          if (cancelled) return;
          if (!r.data.items || r.data.items.length === 0) {
            setActiveStoreId(null);
            setError("no_store");
            setLoading(false);
            return;
          }
          const firstId = r.data.items[0].id;
          setActiveStoreId(firstId);
          fetchData(firstId);
        })
        .catch(() => {
          if (!cancelled) {
            setError("fetch_error");
            setLoading(false);
          }
        });
    }

    return () => { cancelled = true; };
  }, [hasHydrated, isLoggedIn, token, activeStoreId, radiusKm, searchCoord, setActiveStoreId]);

  // 2026-05-25 — 반경 안 매장 엑셀 다운로드
  const downloadXlsx = async () => {
    if (!activeStoreId || downloadingXlsx) return;
    setDownloadingXlsx(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({ radius_km: String(radiusKm) });
      if (searchCoord) {
        params.set("lat", String(searchCoord.lat));
        params.set("lng", String(searchCoord.lng));
        params.set("label", searchCoord.label);
      }
      const r = await axios.get(
        `/api/stores/${activeStoreId}/neighborhood/export.xlsx?${params}`,
        { headers, responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] as string | undefined;
      const m = cd?.match(/filename\*=UTF-8''([^;]+)/);
      a.download = m ? decodeURIComponent(m[1]) : `neighborhood_${radiusKm}km.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("엑셀 다운로드 실패: " + String(e));
    } finally {
      setDownloadingXlsx(false);
    }
  };

  // 5/11 #4 — 카카오 SDK Places 키워드 검색 (지역명 → 좌표)
  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    if (!sdkReady) {
      setSearchError("지도 SDK 로딩 중. 잠시 후 다시 시도해 주세요.");
      return;
    }
    setSearchError(null);
    setSearchLoading(true);
    const kakao = (window as any).kakao;
    const ps = new kakao.maps.services.Places();
    ps.keywordSearch(q, (results: any[], status: string) => {
      setSearchLoading(false);
      if (status !== kakao.maps.services.Status.OK || !results.length) {
        setSearchError("검색 결과가 없어요. 더 정확하게 입력해주세요.");
        return;
      }
      const top = results[0];
      setSearchCoord({
        lat: parseFloat(top.y),
        lng: parseFloat(top.x),
        label: top.place_name || q,
      });
    });
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchCoord(null);
    setSearchError(null);
  };

  // 카카오맵 렌더링 (SDK 준비 + 데이터 도착 후)
  useEffect(() => {
    if (!sdkReady || !data?.has_coord || !data.center.latitude || !data.center.longitude) return;
    if (!mapRef.current) return;

    const kakao = (window as any).kakao;
    const center = new kakao.maps.LatLng(data.center.latitude, data.center.longitude);

    if (!mapInstance.current) {
      mapInstance.current = new kakao.maps.Map(mapRef.current, {
        center,
        level: radiusKm <= 0.5 ? 4 : radiusKm <= 1 ? 5 : radiusKm <= 2 ? 6 : 7,
      });
    } else {
      mapInstance.current.setCenter(center);
      mapInstance.current.setLevel(
        radiusKm <= 0.5 ? 4 : radiusKm <= 1 ? 5 : radiusKm <= 2 ? 6 : 7,
      );
    }

    // 5/11 #2 — 지도 클릭 시 그 좌표 기준 동네 분석 (검색 박스 대안)
    // 5/11 #4 (사용자 결정) — 즉시 재분석 X → popup 띄우고 사용자 확정 (PC/모바일 일관)
    kakao.maps.event.removeListener(mapInstance.current, "click");
    kakao.maps.event.addListener(mapInstance.current, "click", (e: any) => {
      // 핀 클릭 직후 trigger 된 map click 무시 (DOM bubble 과 별개 시스템)
      if (pinJustClickedRef.current) return;
      const lat = e.latLng.getLat();
      const lng = e.latLng.getLng();
      // 좌표 → 주소 변환 (Geocoder.coord2Address)
      const geocoder = new kakao.maps.services.Geocoder();
      geocoder.coord2Address(lng, lat, (result: any[], status: string) => {
        // 비동기 callback 도 ref 체크 (200ms 안에 callback 도착 가능)
        if (pinJustClickedRef.current) return;
        const ok = status === kakao.maps.services.Status.OK && result.length > 0;
        const top = ok ? result[0] : null;
        const addrName = top
          ? (top.road_address?.address_name || top.address?.address_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`)
          : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        setSelectedPin(null);
        setSelectedBlank({ lat, lng, addrName });
      });
    });

    // 옛 overlay 제거
    overlaysRef.current.forEach((o) => o.setMap?.(null));
    overlaysRef.current = [];

    // 본인 매장 마커 (amber)
    const myMarker = new kakao.maps.CustomOverlay({
      position: center,
      content: `<div style="background:${PIN_COLOR.myStore};width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>`,
      yAnchor: 0.5,
      xAnchor: 0.5,
      zIndex: 100,
    });
    myMarker.setMap(mapInstance.current);
    overlaysRef.current.push(myMarker);

    // 반경 원 — 5/11 #2 강화 (사용자 결정: 점선·흐릿 → 실선·명확 + 라벨)
    // outer ring (white halo) — 어두운 지도 / 밝은 지도 모두 명확
    const circleHalo = new kakao.maps.Circle({
      center,
      radius: radiusKm * 1000,
      strokeWeight: 6,
      strokeColor: "#FFFFFF",
      strokeOpacity: 0.85,
      strokeStyle: "solid",
      fillColor: "#FF5C35",
      fillOpacity: 0,
    });
    circleHalo.setMap(mapInstance.current);
    overlaysRef.current.push(circleHalo);

    // inner ring (brand primary)
    const circle = new kakao.maps.Circle({
      center,
      radius: radiusKm * 1000,
      strokeWeight: 3,
      strokeColor: "#FF5C35",
      strokeOpacity: 1,
      strokeStyle: "solid",
      fillColor: "#FF5C35",
      fillOpacity: 0.08,
    });
    circle.setMap(mapInstance.current);
    overlaysRef.current.push(circle);

    // 반경 라벨 (원 우상단) — 사용자가 km 변경 시 즉각 시각 피드백
    // 반경 = N km 만큼 위도 offset (1도 ≈ 111km, 45도 방향 ≈ √2/2)
    const offsetDeg = (radiusKm / 111) * 0.707;
    const labelPos = new kakao.maps.LatLng(
      center.getLat() + offsetDeg,
      center.getLng() + offsetDeg,
    );
    const radiusLabel = new kakao.maps.CustomOverlay({
      position: labelPos,
      content: `<div style="background:#FF5C35;color:#fff;font-size:11px;font-weight:700;padding:3px 8px;border-radius:10px;box-shadow:0 2px 6px rgba(0,0,0,0.4);white-space:nowrap;border:2px solid #fff;">반경 ${radiusKm < 1 ? `${radiusKm * 1000}m` : `${radiusKm}km`}</div>`,
      yAnchor: 0.5,
      xAnchor: 0.5,
      zIndex: 50,
    });
    radiusLabel.setMap(mapInstance.current);
    overlaysRef.current.push(radiusLabel);

    // 카페 핀 — PC: hover tooltip / 모바일: tap → popup (#9, 2026-05-11)
    // 전역 handler 로 React state 와 연결 (kakao CustomOverlay 의 onclick 에서 호출)
    // 2026-05-12 fix — places 500 cap 외 recent_new/recent_closed 도 마커로 그림 (사용자 보고)
    const allPlacesMap = new Map<number, NeighborhoodPlace>();
    data.places.forEach((p) => allPlacesMap.set(p.id, p));
    (data.recent_new ?? []).forEach((p) => { if (!allPlacesMap.has(p.id)) allPlacesMap.set(p.id, p); });
    (data.recent_closed ?? []).forEach((p) => { if (!allPlacesMap.has(p.id)) allPlacesMap.set(p.id, p); });
    const allPlaces = Array.from(allPlacesMap.values());
    (window as unknown as { __selectMapPin?: (id: number) => void }).__selectMapPin = (id: number) => {
      const place = allPlacesMap.get(id);
      if (place) {
        // 카카오 map click 이벤트도 곧바로 trigger → ref 200ms 안 무시 처리
        pinJustClickedRef.current = true;
        setTimeout(() => { pinJustClickedRef.current = false; }, 200);
        setSelectedBlank(null);   // 빈 지도 popup 떠있으면 닫음 (둘 동시 X)
        setSelectedPin(place);
      }
    };
    allPlaces.forEach((p) => {
      // 2026-05-12 — 통계 카드 필터 적용
      if (pinFilter === "new" && !p.is_new) return;
      if (pinFilter === "closed" && !p.is_closed_recent) return;
      if (pinFilter === "active" && (p.is_new || p.is_closed_recent)) return;
      const color = p.is_closed_recent ? PIN_COLOR.closed : p.is_new ? PIN_COLOR.new : PIN_COLOR.active;
      // 2026-05-25 사용자 피드백 — 클릭 정확도 ↑ 위해 마커 크기 ~50% 증가
      // 일반 11→16, 신규/폐업 14→20
      const size = p.is_new || p.is_closed_recent ? 20 : 16;
      // 모바일/PC 클릭 용 hit area — 투명 padding 으로 hit box 만 확장 (시각은 그대로)
      const hit = 6;   // size+hit*2 ≈ 28~32px (Apple HIG 권장 44px 가깝게)
      const marker = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(p.latitude, p.longitude),
        content: `<div title="${p.biz_name}" data-pin="${p.id}" onclick="event.stopPropagation(); window.__selectMapPin && window.__selectMapPin(${p.id})" style="padding:${hit}px;margin:-${hit}px;cursor:pointer;line-height:0;"><div style="background:${color};width:${size}px;height:${size}px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div></div>`,
        yAnchor: 0.5,
        xAnchor: 0.5,
      });
      marker.setMap(mapInstance.current);
      overlaysRef.current.push(marker);
    });

    return () => {
      overlaysRef.current.forEach((o) => o.setMap?.(null));
      overlaysRef.current = [];
    };
  }, [sdkReady, data, radiusKm, pinFilter]);

  // ── UX 분기 ──

  if (!hasHydrated) {
    return <div className="px-4 py-12 text-center"><Skeleton className="h-64 w-full" /></div>;
  }

  if (!isLoggedIn) {
    return (
      <div className="px-4 py-12 text-center max-w-md mx-auto space-y-6">
        <div className="text-5xl">🗺</div>
        <div className="space-y-2">
          <h1 className="text-xl font-black text-text">내 동네 카페 레이더</h1>
          <p className="text-sm text-text-sub leading-relaxed">
            본인 매장 주변 카페 분포, 신규 개업·폐업을 한눈에.
          </p>
        </div>
        <Link href="/login" className="inline-block btn-primary text-xs px-4 py-2">
          로그인하고 매장 등록하기 →
        </Link>
      </div>
    );
  }

  if (error === "no_store") {
    return (
      <div className="px-4 py-12 text-center max-w-md mx-auto space-y-6">
        <div className="text-5xl">🗺</div>
        <h1 className="text-xl font-black text-text">매장 등록이 먼저예요</h1>
        <p className="text-sm text-text-sub leading-relaxed">
          매장 주소를 등록하면 동네 레이더가 본인 매장 주변 카페 분포·신규·폐업을 보여드려요.
        </p>
        <Link href="/store" className="inline-block btn-primary text-xs px-4 py-2">매장 등록하기 →</Link>
      </div>
    );
  }

  if (error === "no_coord") {
    return (
      <div className="px-4 py-12 text-center max-w-md mx-auto space-y-6">
        <div className="text-5xl">📍</div>
        <h1 className="text-xl font-black text-text">매장 주소가 필요해요</h1>
        <p className="text-sm text-text-sub leading-relaxed">
          {data?.store_name ?? "내 매장"} 의 주소가 등록되지 않았어요. 매장 관리에서 주소를 입력하면
          동네 레이더가 본인 매장 좌표 기준으로 동작합니다.
        </p>
        <Link href="/store/manage" className="inline-block btn-primary text-xs px-4 py-2">
          매장 주소 입력 →
        </Link>
      </div>
    );
  }

  // Skeleton 은 첫 진입 (data 없음) 에만 노출. 반경 변경 시 data 유지 → 지도 그대로 두고 inline 로딩 표시
  // (사용자 결정 2026-05-10 — 반경 클릭 시 mapRef 재마운트로 mapInstance stale 참조 → client-side exception fix)
  // 5/11 #7 — Skeleton 대신 명확한 로딩 안내 (spinner + 안내문) 페이지 진입 시 즉시 노출
  if (!data && !error) {
    return (
      <div className="px-4 py-16 flex flex-col items-center justify-center gap-4">
        <div className="text-5xl animate-spin">🌀</div>
        <p className="text-base font-bold text-text">동네 레이더 불러오는 중...</p>
        <p className="text-2xs text-text-sub text-center leading-relaxed max-w-xs">
          매장 주변 카페·점포·인구·임대료 데이터를<br />
          한 번에 가져오고 있어요.
          <br />
          <span className="text-text-sub/70 mt-1 inline-block">처음 진입 시 5~10초 정도 소요됩니다</span>
        </p>
      </div>
    );
  }

  if (error === "fetch_error" || !data) {
    return (
      <div className="px-4 py-12 text-center text-text-sub">
        데이터를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
      </div>
    );
  }

  // 정상 — 풀 동네 레이더 (베타 종료 후 free_auth 는 카드 블러 + CTA)
  return (
    <div className="px-4 py-4 space-y-4 relative">
      <div>
        <h1 className="text-xl font-black text-text tracking-tight">
          🗺 {searchCoord ? "검색 위치" : data.store_name} 동네 레이더
          {searchCoord && <span className="text-2xs font-semibold text-primary ml-2 align-middle">검색</span>}
        </h1>
        <p className="text-2xs text-text-sub mt-0.5">{data.data_freshness_note}</p>
      </div>

      {/* free_auth (베타 종료 후) — 블러 + 잠금 CTA (사용자 결정 2026-05-22) */}
      {!hasFullAccess && (
        <Link
          href="/mypage/subscribe"
          className="block sticky top-2 z-50 mx-auto max-w-md rounded-xl border border-primary/30 bg-primary/10 backdrop-blur px-4 py-3 text-center shadow-md hover:bg-primary/15"
        >
          <p className="text-sm font-bold text-primary">🔒 구독하면 동네 레이더 풀버전 보기</p>
          <p className="text-2xs text-text-sub mt-1">반경 점포·신규·폐업·인구·임대료 4 카드 + 지도</p>
        </Link>
      )}

      <div className={!hasFullAccess ? "blur-sm pointer-events-none select-none" : ""}>
      <div className="space-y-4">

      {/* 5/11 #4 — 다른 지역 검색 */}
      <div className="card p-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            placeholder="지역·역·주소 검색 (예: 강남역, 성수동)"
            className="flex-1 text-sm bg-bg-secondary text-text px-3 py-2 rounded-lg border border-border outline-none focus:border-primary/50"
          />
          <button
            onClick={handleSearch}
            disabled={!searchQuery.trim() || searchLoading}
            className="text-xs font-bold text-bg bg-primary px-3 py-2 rounded-lg disabled:opacity-50"
          >
            {searchLoading ? "..." : "검색"}
          </button>
          {searchCoord && (
            <button
              onClick={clearSearch}
              className="text-xs font-semibold text-text-sub border border-border px-3 py-2 rounded-lg hover:text-text"
              title="내 매장 좌표로 돌아가기"
            >
              ↺ 내 매장
            </button>
          )}
        </div>
        {searchError && <p className="text-2xs text-red-500 mt-2">{searchError}</p>}
        {/* 5/11 후속 — 안내문 중복 제거 (검색 박스에 주소가 이미 표시됨). 헤더는 "검색 위치 동네 레이더" 로 단순화 */}
      </div>

      {/* 다매장 selector — 검색 모드일 때 숨김 (매장 무관 상권 분석) */}
      {!searchCoord && <ActiveStoreSelector inline />}

      {/* 통계 카드 — 3 그리드 (클릭 시 마커 필터, 사용자 결정 2026-05-12) */}
      <p className="text-2xs text-text-sub text-center -mb-1">
        💡 카드를 <span className="font-semibold text-text">클릭</span>하면 해당 매장만 지도에 표시됩니다
      </p>
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => setPinFilter((f) => (f === "active" ? "all" : "active"))}
          className={`card p-3 text-left transition-colors ${pinFilter === "active" ? "ring-2 ring-blue-400 bg-bg-elevated" : "hover:bg-bg-elevated"}`}
        >
          <p className="text-2xs text-text-sub">영업 중 {pinFilter === "active" && <span className="text-blue-400">●</span>}</p>
          <p className="text-lg font-bold text-text mt-0.5">{data.stats.total_active.toLocaleString()}</p>
          <p className="text-2xs text-text-sub">반경 {data.radius_km}km</p>
        </button>
        <button
          type="button"
          onClick={() => setPinFilter((f) => (f === "new" ? "all" : "new"))}
          style={{ borderLeft: `3px solid ${PIN_COLOR.new}` }}
          className={`card p-3 text-left transition-colors ${pinFilter === "new" ? "ring-2 ring-emerald-400 bg-bg-elevated" : "hover:bg-bg-elevated"}`}
        >
          <p className="text-2xs text-text-sub">최근 3개월 신규 {pinFilter === "new" && <span className="text-emerald-400">●</span>}</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: PIN_COLOR.new }}>+{data.stats.new_3m}</p>
        </button>
        <button
          type="button"
          onClick={() => setPinFilter((f) => (f === "closed" ? "all" : "closed"))}
          style={{ borderLeft: `3px solid ${PIN_COLOR.closed}` }}
          className={`card p-3 text-left transition-colors ${pinFilter === "closed" ? "ring-2 ring-red-400 bg-bg-elevated" : "hover:bg-bg-elevated"}`}
        >
          <p className="text-2xs text-text-sub">최근 3개월 폐업 {pinFilter === "closed" && <span className="text-red-400">●</span>}</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: PIN_COLOR.closed }}>-{data.stats.closed_3m}</p>
        </button>
      </div>
      {pinFilter !== "all" && (
        <p className="text-2xs text-text-sub text-center -mt-1">
          {pinFilter === "active" ? "🔵 영업 중" : pinFilter === "new" ? "🟢 신규 매장" : "🔴 폐업 매장"} 만 표시 중 — 카드 다시 누르면 전체
        </p>
      )}

      {/* 반경 슬라이더 + 엑셀 다운로드 (2026-05-25) */}
      <div className="card p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-2xs text-text-sub">반경 설정</p>
          {loading && (
            <span className="flex items-center gap-1.5 text-2xs text-text-sub">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse" />
              불러오는 중...
            </span>
          )}
        </div>
        <div className="flex gap-1.5">
          {RADIUS_OPTIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRadiusKm(r)}
              disabled={loading}
              className={`flex-1 text-xs py-1.5 rounded border transition-colors disabled:opacity-50 ${
                radiusKm === r
                  ? "bg-primary text-bg border-primary font-bold"
                  : "border-border text-text-sub hover:text-text"
              }`}
            >
              {r}km
            </button>
          ))}
        </div>
        <button
          onClick={downloadXlsx}
          disabled={loading || downloadingXlsx || !activeStoreId}
          className="w-full mt-2 text-2xs py-2 border border-primary/40 text-primary rounded hover:bg-primary/10 disabled:opacity-50"
        >
          {downloadingXlsx ? "다운로드 중..." : "📥 반경 매장 엑셀 다운로드 (반경 안 전체)"}
        </button>
      </div>

      {/* 5/11 — 지도 위 클릭 안내 (PC/모바일 일관) */}
      <div className="text-2xs text-text-sub px-1 space-y-0.5">
        <p>
          📌 지도 위 아무 곳이나 또는 점(핀)을 <span className="font-semibold text-text">클릭(또는 탭)</span>하면 위치 정보 + <span className="font-semibold text-text">&ldquo;이 위치로 동네 분석&rdquo;</span> 버튼이 표시됩니다.
        </p>
      </div>

      {/* 지도 — 5/11 #7 로딩 안내 강화 */}
      <div className="card p-0 overflow-hidden">
        {!KAKAO_JS_KEY ? (
          <div className="h-96 flex items-center justify-center text-text-sub text-sm">
            카카오맵 키 미설정 (운영자 점검 필요)
          </div>
        ) : !sdkReady ? (
          <div className="h-96 flex flex-col items-center justify-center gap-3 px-6">
            <div className="text-4xl animate-spin">🌀</div>
            <p className="text-sm font-bold text-text">지도를 불러오는 중...</p>
            <p className="text-2xs text-text-sub text-center leading-relaxed">
              매장 주변 카페·점포 데이터를 한 번에 가져오느라<br />
              <span className="text-text-sub/70">5~10초 정도 소요됩니다</span>
            </p>
          </div>
        ) : (
          <div ref={mapRef} className="w-full h-96" />
        )}
      </div>

      {/* 범례 */}
      <div className="card p-3">
        <p className="text-2xs font-semibold text-text mb-2">범례</p>
        <div className="flex flex-wrap gap-3 text-2xs">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: PIN_COLOR.myStore, border: "2px solid white", boxShadow: "0 0 0 1px #ccc" }} />
            본인 매장
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: PIN_COLOR.new }} />
            🆕 최근 3개월 신규
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: PIN_COLOR.closed }} />
            최근 3개월 폐업
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: PIN_COLOR.active }} />
            영업 중
          </span>
        </div>
      </div>

      {/* ── 📊 상세 상권 분석 (#8, 사용자 결정 2026-05-10) ── */}
      <DetailedAnalysisSection data={data} />

      {/* ── 그룹 ③ 변화 추세 (신규 + 폐업, 2026-05-15 가독성 개선) ── */}
      {((data.recent_new ?? []).length > 0 || (data.recent_closed ?? []).length > 0) && (
        <section className="space-y-3">
          <h2 className="section-title-major mt-2">📈 변화 추세</h2>

          {(data.recent_new ?? []).length > 0 && (
            <div className="card p-3">
              <h3 className="text-sm font-bold text-text mb-2">최근 3개월 신규 ({data.stats.new_3m})</h3>
              <div className="space-y-2">
                {(data.recent_new ?? []).slice(0, 10).map((p) => {
                  const cat = p.biz_category ?? "";
                  const isCafeOrBakery = /카페|커피|음료|제과|베이커리|디저트/.test(cat);
                  return (
                    <div key={p.id} className={`flex items-start justify-between gap-2 py-1.5 border-b border-border last:border-0 ${isCafeOrBakery ? "" : "opacity-50"}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isCafeOrBakery && (
                            <span className="text-2xs font-bold text-emerald-400 bg-emerald-400/10 px-1 py-0.5 rounded shrink-0">카페</span>
                          )}
                          <p className="text-xs font-semibold text-text truncate">{p.biz_name}</p>
                        </div>
                        <p className="text-2xs text-text-sub truncate mt-0.5">
                          {cat || "—"} · {p.address_road ?? ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-2xs font-semibold" style={{ color: PIN_COLOR.new }}>{p.distance_m}m</p>
                        <p className="text-2xs text-text-sub">{p.approval_date?.slice(0, 7)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(data.recent_closed ?? []).length > 0 && (
            <div className="card p-3">
              <h3 className="text-sm font-bold text-text mb-2">최근 3개월 폐업 ({data.stats.closed_3m})</h3>
              <div className="space-y-2">
                {(data.recent_closed ?? []).slice(0, 10).map((p) => {
                  const cat = p.biz_category ?? "";
                  const isCafeOrBakery = /카페|커피|음료|제과|베이커리|디저트/.test(cat);
                  return (
                    <div key={p.id} className={`flex items-start justify-between gap-2 py-1.5 border-b border-border last:border-0 ${isCafeOrBakery ? "" : "opacity-50"}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isCafeOrBakery && (
                            <span className="text-2xs font-bold text-red-400 bg-red-400/10 px-1 py-0.5 rounded shrink-0">카페</span>
                          )}
                          <p className="text-xs font-semibold text-text-sub line-through truncate">{p.biz_name}</p>
                        </div>
                        <p className="text-2xs text-text-sub truncate mt-0.5">
                          {cat || "—"} · {p.address_road ?? ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-2xs font-semibold" style={{ color: PIN_COLOR.closed }}>{p.distance_m}m</p>
                        <p className="text-2xs text-text-sub">{p.closure_date?.slice(0, 7)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* #9 모바일 핀 tap popup (2026-05-11) — PC hover tooltip 보조 */}
      {selectedPin && (
        <div className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="card border-2 border-primary/60 shadow-xl bg-bg-elevated p-3">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                    {selectedPin.service_type === "bakery" ? "베이커리" : "카페"}
                  </span>
                  {selectedPin.is_new && (
                    <span className="text-2xs font-bold text-emerald-500">🆕 신규</span>
                  )}
                  {selectedPin.is_closed_recent && (
                    <span className="text-2xs font-bold text-red-500">폐업</span>
                  )}
                </div>
                <p className="text-sm font-bold text-text truncate">{selectedPin.biz_name}</p>
                {selectedPin.address_road && (
                  <p className="text-2xs text-text-sub mt-0.5 truncate">📍 {selectedPin.address_road}</p>
                )}
                {/* 2026-05-25 — 부가 정보 (사업자개시일자/영업기간/세부업태/거리/폐업일자) */}
                <div className="mt-1.5 space-y-0.5 text-2xs text-text-sub">
                  {selectedPin.biz_category && (
                    <p>🏷 <span className="text-text">{selectedPin.biz_category}</span></p>
                  )}
                  {selectedPin.approval_date && (
                    <p>
                      📅 개업 <span className="text-text">{selectedPin.approval_date}</span>
                      <span className="ml-1 opacity-70">({yearsSince(selectedPin.approval_date)})</span>
                    </p>
                  )}
                  {selectedPin.is_closed_recent && selectedPin.closure_date && (
                    <p>⛔ 폐업 <span className="text-red-400">{selectedPin.closure_date}</span></p>
                  )}
                  {typeof selectedPin.distance_m === "number" && selectedPin.distance_m > 0 && (
                    <p>📐 내 매장에서 <span className="text-text">{formatDistance(selectedPin.distance_m)}</span></p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedPin(null)}
                className="text-text-sub hover:text-text text-lg leading-none shrink-0"
                aria-label="닫기"
              >
                ×
              </button>
            </div>
            <button
              onClick={() => {
                const label = selectedPin.biz_name || "선택 위치";
                setSearchCoord({ lat: selectedPin.latitude, lng: selectedPin.longitude, label });
                setSearchQuery(label);
                setSelectedPin(null);
              }}
              className="w-full text-xs font-bold py-2 rounded-lg bg-primary text-bg"
            >
              📍 이 위치로 동네 분석
            </button>
          </div>
        </div>
      )}

      {/* #4 빈 지도 클릭 popup (2026-05-11) — PC/모바일 일관 (옛: 즉시 재분석 → 신: 사용자 확정) */}
      {selectedBlank && (
        <div className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="card border-2 border-primary/60 shadow-xl bg-bg-elevated p-3">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-2xs text-text-sub mb-0.5">📌 선택한 위치</p>
                <p className="text-sm font-bold text-text break-words leading-snug">{selectedBlank.addrName}</p>
              </div>
              <button
                onClick={() => setSelectedBlank(null)}
                className="text-text-sub hover:text-text text-lg leading-none shrink-0"
                aria-label="닫기"
              >
                ×
              </button>
            </div>
            <button
              onClick={() => {
                setSearchCoord({ lat: selectedBlank.lat, lng: selectedBlank.lng, label: selectedBlank.addrName });
                setSearchQuery(selectedBlank.addrName);
                setSearchError(null);
                setSelectedBlank(null);
              }}
              className="w-full text-xs font-bold py-2 rounded-lg bg-primary text-bg"
            >
              📍 이 위치로 동네 분석
            </button>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   📊 상세 상권 분석 섹션 (#8, 사용자 결정 2026-05-10)
   인구통계 + 점포 + 임대료 + 유동인구 4 카드
   ────────────────────────────────────────────── */

function fmtPeriodMonth(p: string | null | undefined): string {
  if (!p || p.length < 6) return "";
  return `${p.slice(0, 4)}.${p.slice(4, 6)}`;
}

function fmtPeriodQuarter(p: string | null | undefined): string {
  if (!p || p.length < 6) return "";
  const y = p.slice(0, 4);
  const q = Math.ceil(parseInt(p.slice(4, 6), 10) / 3);
  return `${y} Q${q}`;
}

function DetailedAnalysisSection({ data }: { data: NeighborhoodOut }) {
  const { demographics, business, living_population, rent } = data;

  // 4 카드 모두 없으면 섹션 자체 숨김
  if (!demographics && !business && !living_population && !rent) return null;

  // 연령 분포 데이터 (큰 순으로 정렬 → 가장 큰 그룹 자동 강조)
  const ageBars = demographics ? [
    { label: "20대 미만", val: demographics.age_under20_pct },
    { label: "20대",      val: demographics.age_20s_pct },
    { label: "30대",      val: demographics.age_30s_pct },
    { label: "40대",      val: demographics.age_40s_pct },
    { label: "50대",      val: demographics.age_50s_pct },
    { label: "60대+",     val: demographics.age_60plus_pct },
  ].filter(a => a.val != null) : [];
  const ageMax = Math.max(...ageBars.map(a => a.val ?? 0), 1);

  return (
    <div className="space-y-5">
      {/* ── 그룹 ① 동네 규모 ────────────────────── */}
      {demographics && demographics.total_population != null && (
        <section>
          <h2 className="section-title-major mt-2">🏘 동네 규모</h2>
          <div className="card p-3">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-bold text-text">동네 인구</h3>
              <span className="text-2xs text-text-sub">
                {demographics.region_name ?? ""} · {fmtPeriodMonth(demographics.period)}
              </span>
            </div>
            <p className="text-2xl font-black text-text">
              {demographics.total_population.toLocaleString()}
              <span className="text-xs font-normal text-text-sub ml-1">명</span>
            </p>
            <p className="text-2xs text-text-sub mt-0.5">
              남 {demographics.male_pct?.toFixed(1)}% · 여 {demographics.female_pct?.toFixed(1)}%
            </p>
            {/* 연령 분포 — 가로 막대 차트 (큰 그룹 자동 강조) */}
            {ageBars.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <p className="text-2xs text-text-sub font-semibold">연령 분포</p>
                {ageBars.map((a) => {
                  const pct = a.val ?? 0;
                  const widthPct = (pct / ageMax) * 100;
                  const isTop = pct === ageMax;
                  return (
                    <div key={a.label} className="flex items-center gap-2">
                      <span className={`text-2xs w-14 shrink-0 ${isTop ? "text-text font-bold" : "text-text-sub"}`}>
                        {a.label}
                      </span>
                      <div className="flex-1 h-4 bg-bg-secondary rounded overflow-hidden">
                        <div
                          className="h-full rounded"
                          style={{
                            width: `${widthPct}%`,
                            backgroundColor: isTop ? "#FF5C35" : "rgba(255, 92, 53, 0.35)",
                          }}
                        />
                      </div>
                      <span className={`text-2xs w-10 text-right shrink-0 ${isTop ? "text-text font-bold" : "text-text-sub"}`}>
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 그룹 ② 시장 형성 ────────────────────── */}
      {(business || rent || living_population) && (
        <section>
          <h2 className="section-title-major mt-2">🏬 시장 형성</h2>
          <div className="space-y-3">
            {/* 동네 점포 — 카페 큰 숫자 강조, 나머지 작게 */}
            {business && (
              <div className="card p-3">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="text-sm font-bold text-text">동네 점포</h3>
                  <span className="text-2xs text-text-sub">반경 {business.radius_km}km</span>
                </div>
                <div className="flex items-end gap-4 mb-3 pb-3 border-b border-border">
                  <div>
                    <p className="text-2xs text-primary font-bold mb-0.5">카페</p>
                    <p className="text-3xl font-black text-primary leading-none">
                      {business.cafe_count.toLocaleString()}
                      <span className="text-xs font-normal text-text-sub ml-1">개</span>
                    </p>
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-1.5">
                    {[
                      { label: "베이커리", val: business.bakery_count },
                      { label: "한식",     val: business.korean_count },
                      { label: "음식",     val: business.restaurant_count },
                      { label: "주점",     val: business.pub_count },
                    ].map((b) => (
                      <div key={b.label} className="flex items-center justify-between text-2xs">
                        <span className="text-text-sub">{b.label}</span>
                        <span className="text-text font-semibold">{b.val.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-2xs text-text-sub/80 leading-relaxed">
                  ⓘ 소상공인진흥공단 분류 — 카페·음료점 비슷한 매장 광범위 포함 (시장 규모 시그널)
                </p>
              </div>
            )}

        {/* 3) 임대료 (부동산원) */}
        {rent && (
          <div className="card p-3">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-bold text-text">임대료</h3>
              {rent.available && rent.period && (
                <span className="text-2xs text-text-sub">{fmtPeriodQuarter(rent.period)}</span>
              )}
            </div>
            {rent.available && rent.rent_krw_per_m2 != null ? (
              <>
                {/* 5/11 — fallback 시 노란 경고 + 안내 강화 (사용자 결정 옵션 A+B) */}
                {rent.is_fallback ? (
                  <div className="mb-2 px-2 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-2xs text-amber-500 font-semibold">
                      ⚠ {rent.zone_full_name} 평균 (참고용)
                    </p>
                    <p className="text-2xs text-text-sub mt-0.5 leading-relaxed">
                      이 지역에 부동산원 표본 상권이 없어 시도 평균을 보여드려요.
                      실제 임대료와 차이가 클 수 있어요.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-text-sub mb-1">
                    {rent.zone_full_name ?? ""}
                    {rent.distance_km != null && (
                      <span className="text-text-sub/70"> · 매장에서 {rent.distance_km.toFixed(1)}km</span>
                    )}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="rounded-lg bg-bg-secondary px-2 py-2 text-center">
                    <p className="text-2xs text-text-sub">평균 임대료</p>
                    <p className="text-base font-bold text-text mt-0.5">
                      {(rent.rent_krw_per_m2).toFixed(1)}
                      <span className="text-2xs font-normal text-text-sub ml-0.5">천원/㎡</span>
                    </p>
                  </div>
                  <div className="rounded-lg bg-bg-secondary px-2 py-2 text-center">
                    <p className="text-2xs text-text-sub">공실률</p>
                    <p className="text-base font-bold text-text mt-0.5">
                      {rent.vacancy_pct != null ? `${rent.vacancy_pct.toFixed(1)}%` : "—"}
                    </p>
                  </div>
                </div>
                <p className="text-2xs text-text-sub mt-2 leading-relaxed">
                  한국부동산원 소규모상가 임대동향 — 가까운 상권 기준
                </p>
              </>
            ) : (
              <p className="text-xs text-text-sub py-2">임대료 데이터 매핑 중</p>
            )}
          </div>
        )}

        {/* 시간대별 유동인구 — 서울 = 차트 / 그 외 = 축소된 회색 안내 1줄 */}
        {living_population && (
          living_population.available && living_population.hourly.length > 0 ? (
            <div className="card p-3">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-bold text-text">시간대별 유동인구</h3>
                <span className="text-2xs text-text-sub">서울 생활인구 (KT)</span>
              </div>
              <p className="text-xs text-text-sub mb-2">
                하루 평균 <span className="text-text font-semibold">{living_population.avg_population?.toLocaleString()}</span>명
              </p>
              <LivingPopulationChart hourly={living_population.hourly} />
            </div>
          ) : (
            // 잠긴 상태 = 큰 카드 X → 회색 1줄 노트
            <p className="px-2 text-2xs text-text-sub/70">
              🔒 시간대별 유동인구 — {living_population.note ?? "서울 외 지역은 준비 중"}
            </p>
          )
        )}
          </div>
        </section>
      )}
    </div>
  );
}

function LivingPopulationChart({ hourly }: { hourly: NeighborhoodLivingPopHour[] }) {
  if (!hourly.length) return null;
  const maxPop = Math.max(...hourly.map((h) => h.population ?? 0), 1);
  const peakHour = hourly.reduce((a, b) => ((b.population ?? 0) > (a.population ?? 0) ? b : a));
  return (
    <>
      {/* 5/11 fix — 옛 코드의 컬럼 div 에 h-full 누락 → inner 막대 height % 계산 실패로 막대 안 보임 */}
      {/* + bg-primary/60 PurgeCSS 누락 우회 → inline backgroundColor */}
      <div className="flex items-end gap-[2px] h-24">
        {hourly.map((h) => {
          const pct = ((h.population ?? 0) / maxPop) * 100;
          const isPeak = h.hour === peakHour.hour;
          return (
            <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full">
              <div
                className="w-full rounded-t"
                style={{
                  height: `${Math.max(4, pct)}%`,
                  backgroundColor: isPeak ? "#FF5C35" : "rgba(255, 92, 53, 0.55)",
                  minHeight: "3px",
                }}
                title={`${h.hour}시 ${h.population?.toLocaleString() ?? "—"}명`}
              />
              {h.hour % 6 === 0 && (
                <span className="text-[9px] text-text-sub mt-1">{h.hour}</span>
              )}
            </div>
          );
        })}
      </div>
      {peakHour.population != null && (
        <p className="text-2xs text-text-sub mt-2 leading-relaxed">
          피크: <span className="text-text font-semibold">{peakHour.hour}시</span> {peakHour.population.toLocaleString()}명
        </p>
      )}
    </>
  );
}
